import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';

const HtmlEditor = ({ value, onChange, height = 240 }) => (
  <textarea
    className="form-control email-html-editor"
    style={{ minHeight: height }}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
  />
);

const SupportEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [layout, setLayout] = useState({ header_html: '', footer_html: '' });
  const [selectedKey, setSelectedKey] = useState('');
  const [template, setTemplate] = useState(null);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [layoutStatus, setLayoutStatus] = useState({ type: '', message: '' });
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sampleData, setSampleData] = useState({});
  const bodyEditorRef = useRef(null);
  const [activeTab, setActiveTab] = useState('layout');

  const placeholderList = useMemo(() => {
    if (!template?.placeholders || !Array.isArray(template.placeholders)) return [];
    return template.placeholders;
  }, [template]);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/acct-mgmt/v1/support/email-templates');
      setTemplates(response.data || []);
      if (!selectedKey && response.data?.length > 0) {
        setSelectedKey(response.data[0].template_key);
      }
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load templates.' });
    }
  };

  const loadLayout = async () => {
    try {
      const response = await api.get('/acct-mgmt/v1/support/email-templates/layout');
      setLayout(response.data || { header_html: '', footer_html: '' });
    } catch (err) {
      setLayoutStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load layout.' });
    }
  };

  const loadTemplate = async (key) => {
    if (!key) return;
    setStatus({ type: '', message: '' });
    try {
      const response = await api.get(`/acct-mgmt/v1/support/email-templates/${key}`);
      setTemplate(response.data);
      const placeholders = Array.isArray(response.data?.placeholders) ? response.data.placeholders : [];
      const nextData = {};
      placeholders.forEach((token) => {
        nextData[token] = '';
      });
      setSampleData(nextData);
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load template.' });
    }
  };

  useEffect(() => {
    loadTemplates();
    loadLayout();
  }, []);

  useEffect(() => {
    if (selectedKey) loadTemplate(selectedKey);
  }, [selectedKey]);

  const updateSample = (token, value) => {
    setSampleData((prev) => ({ ...prev, [token]: value }));
  };

  const insertPlaceholder = (token) => {
    if (!token) return;
    const textarea = bodyEditorRef.current;
    if (!textarea) return;
    const insertValue = `{{${token}}}`;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const nextValue = `${template.body_html || ''}`.slice(0, start) + insertValue + `${template.body_html || ''}`.slice(end);
    setTemplate((prev) => ({ ...prev, body_html: nextValue }));
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + insertValue.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const saveLayout = async () => {
    setLayoutStatus({ type: '', message: '' });
    try {
      await api.put('/acct-mgmt/v1/support/email-templates/layout', layout);
      setLayoutStatus({ type: 'success', message: 'Layout updated.' });
    } catch (err) {
      setLayoutStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update layout.' });
    }
  };

  const saveTemplate = async () => {
    if (!template) return;
    setSaving(true);
    setStatus({ type: '', message: '' });
    try {
      await api.put(`/acct-mgmt/v1/support/email-templates/${template.template_key}`, {
        subject: template.subject,
        body_html: template.body_html,
        placeholders: template.placeholders,
        required_placeholders: template.required_placeholders,
        is_active: template.is_active,
      });
      setStatus({ type: 'success', message: 'Template updated.' });
      loadTemplates();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update template.' });
    } finally {
      setSaving(false);
    }
  };

  const previewTemplate = async () => {
    if (!template) return;
    setStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/support/email-templates/${template.template_key}/preview`, {
        data: sampleData,
      });
      setPreview({ subject: response.data.subject, html: response.data.html });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to preview template.' });
    }
  };

  const sendTestEmail = async () => {
    if (!template || !testEmail) return;
    setSendingTest(true);
    setStatus({ type: '', message: '' });
    try {
      await api.post(`/acct-mgmt/v1/support/email-templates/${template.template_key}/test`, {
        to: testEmail,
        data: sampleData,
      });
      setStatus({ type: 'success', message: 'Test email sent.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send test email.' });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Email Templates</h2>
          <div className="text-muted">Manage global email content and layout.</div>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'layout' ? 'active' : ''}`}
            onClick={() => setActiveTab('layout')}
          >
            Global Layout Settings
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveTab('templates')}
          >
            Email Template Settings
          </button>
        </li>
      </ul>

      {activeTab === 'layout' && (
        <>
          {layoutStatus.message && (
            <div className={`alert alert-${layoutStatus.type}`} role="alert">{layoutStatus.message}</div>
          )}
          <div className="card mb-3">
            <div className="card-body">
              <div className="card-title d-flex align-items-center justify-content-between w-100 mb-3">
                <h5 className="mb-0">Global Layout</h5>
              </div>
              <div className="mb-3">
                <label className="form-label">Header HTML</label>
                <HtmlEditor
                  value={layout.header_html}
                  onChange={(value) => setLayout((prev) => ({ ...prev, header_html: value }))}
                  height={200}
                />
              </div>
              <div>
                <label className="form-label">Footer HTML</label>
                <HtmlEditor
                  value={layout.footer_html}
                  onChange={(value) => setLayout((prev) => ({ ...prev, footer_html: value }))}
                  height={200}
                />
              </div>
              <div className="mt-3">
                <button className="btn btn-primary" type="button" onClick={saveLayout}>Save Layout</button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'templates' && (
        <>
          {status.message && (
            <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
          )}
          <div className="card">
            <div className="card-body">
              <div className="card-title d-flex flex-wrap align-items-center justify-content-between w-100 mb-3">
                <h5 className="mb-0">Template Editor</h5>
              </div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div className="flex-grow-1">
                  <label className="form-label">Template</label>
                  <select
                    className="form-select"
                    value={selectedKey}
                    onChange={(e) => setSelectedKey(e.target.value)}
                  >
                    <option value="" disabled>Select a template</option>
                    {templates.map((item) => (
                      <option key={item.template_key} value={item.template_key}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

          {!template ? (
            <div className="text-muted">Select a template to edit.</div>
          ) : (
            <>
              <div className="mb-3">
                <div className="fw-semibold">{template.name}</div>
                <div className="text-muted small">{template.template_key}</div>
              </div>

              <div className="mb-3">
                {template && (
                  <div className="form-check form-switch finbase-toggle mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="template-active"
                      checked={template.is_active !== false}
                      onChange={(e) => setTemplate((prev) => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="template-active">Active</label>
                  </div>
                )}
                <label className="form-label">Subject</label>
                <input
                  className="form-control"
                  value={template.subject || ''}
                  onChange={(e) => setTemplate((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                {placeholderList.length > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-select form-select-sm"
                      onChange={(e) => {
                        insertPlaceholder(e.target.value);
                        e.target.value = '';
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Insert placeholder</option>
                      {placeholderList.map((token) => (
                        <option key={token} value={token}>{token}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Body</label>
                <textarea
                  ref={bodyEditorRef}
                  className="form-control email-html-editor"
                  style={{ minHeight: 320 }}
                  value={template.body_html || ''}
                  onChange={(e) => setTemplate((prev) => ({ ...prev, body_html: e.target.value }))}
                />
              </div>

              {placeholderList.length > 0 && (
                <div className="mb-3">
                  <h5 className="h6 fw-bold mb-2">Sample Data</h5>
                  <div className="row g-2">
                    {placeholderList.map((token) => (
                      <div className="col-md-6" key={token}>
                        <label className="form-label">{token}</label>
                        <input
                          className="form-control"
                          value={sampleData[token] || ''}
                          onChange={(e) => updateSample(token, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(template.required_placeholders || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-muted small mb-2">Required placeholders</div>
                  <div className="d-flex flex-wrap gap-2">
                    {(template.required_placeholders || []).map((token) => (
                      <span key={token} className="badge bg-light text-dark border">
                        {token}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex flex-wrap gap-2">
                <button className="btn btn-primary" type="button" onClick={saveTemplate} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button className="btn btn-outline-primary" type="button" onClick={previewTemplate}>Preview</button>
                <div className="input-group email-test-input">
                  <input
                    className="form-control"
                    placeholder="Test email address"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={sendTestEmail}
                    disabled={sendingTest || !testEmail}
                  >
                    {sendingTest ? 'Sending...' : 'Send Test'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
        </>
      )}

      {preview && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Preview</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setPreview(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="fw-semibold mb-2">Subject: {preview.subject}</div>
                  <div className="email-preview" dangerouslySetInnerHTML={{ __html: preview.html }} />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setPreview(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SupportEmailTemplates;
